/**
 * `authStore` (06 §1.4): the session status and the signed-in user, from `GET /api/me`. Probed at boot and whenever
 * the tab becomes visible again. While signed in, preference changes sync to the server (03 §3.3).
 */
import { effect, signal } from '@preact/signals';
import type { MsgKey } from '@tarock/i18n';
import type { Me, MeUser, UserPrefs } from '@tarock/protocol';
import { api } from '../net/api';
import { applyServerPrefs, prefsStore, syncedPrefs } from './prefs';

/** 06 §1.4's states. */
export type AuthStatus = 'unknown' | 'signedOut' | 'twoFactorPending' | 'signedIn';

/** The session state. */
export const authStore = {
  status: signal<AuthStatus>('unknown'),
  user: signal<MeUser | null>(null),
  /** Why the server ended the session (a ban or a revocation); `/login` shows it until the next sign-in. */
  notice: signal<MsgKey | null>(null),
};

const PREFS_SYNC_MS = 800;
let applying = false;

/** Sets the user after a probe or a sign-in; the server's preferences win. */
export function setUser(user: MeUser | null): void {
  authStore.user.value = user;
  if (user) {
    authStore.notice.value = null;
    applying = true;
    applyServerPrefs(user.prefs);
    applying = false;
    authStore.status.value = 'signedIn';
  } else if (authStore.status.value !== 'twoFactorPending') {
    authStore.status.value = 'signedOut';
  }
}

/** Reads `/api/me`. Offline (or after the 30 s call timeout), the status stays as it was; returns whether it answered. */
export async function probeAuth(): Promise<boolean> {
  const r = await api<Me>('/api/me');
  if (r.ok) {
    setUser(r.data.user);
  } else if (r.code !== 'network') {
    setUser(null);
  }
  return r.ok || r.code !== 'network';
}

/**
 * The server ended the session under a socket (a ban, a revocation, or a 401 at the upgrade; 04 §7.8): forget the
 * user at once, so `/login` does not bounce straight back to the page on a stale `signedIn`, and keep why.
 */
export function sessionEnded(notice: MsgKey | null): void {
  setUser(null);
  authStore.notice.value = notice;
}

/** Marks the password step done while the TOTP challenge is pending (03 §2.4). */
export function setTwoFactorPending(): void {
  authStore.user.value = null;
  authStore.status.value = 'twoFactorPending';
}

/** Signs out (03 §2.2) and forgets the user. */
export async function signOut(): Promise<void> {
  await api('/api/auth/sign-out', { body: {} });
  authStore.status.value = 'signedOut';
  setUser(null);
}

/** A same-origin path for `?next=`, or null (03 §0.3). */
export function safeNext(next: string | null | undefined): string | null {
  return next && /^\/(?![/\\])/.test(next) ? next : null;
}

let syncTimer: ReturnType<typeof setTimeout> | undefined;

/** Boot: the first probe, re-probes on return to the tab, and the preference sync. */
export function initAuth(): void {
  // a first probe that fails on the network (or hangs until the timeout) is retried once: until one answers, a
  // signed-in `/` cannot tell where to go
  void probeAuth().then((answered) => answered || probeAuth());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void probeAuth();
    }
  });
  effect(() => {
    const prefs = syncedPrefs(prefsStore.value);
    const user = authStore.user.peek();
    if (applying || !user || user.impersonatedBy) {
      return;
    }
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      void api('/api/profile', { method: 'PATCH', body: { prefs: prefs as UserPrefs } });
    }, PREFS_SYNC_MS);
  });
}
